/**
 * Synchronous, React-independent logout notification.
 *
 * On sign-out the whole authenticated tree unmounts (ProtectedRoute redirects),
 * so a component watching `user` cannot react before it is torn down — a
 * component being unmounted in a commit runs its cleanup, never a fresh effect.
 * Emitting here from the auth boundary BEFORE React commits that unmount lets
 * shell-owned resource holders run a local-only cleanup pass while still
 * mounted; authenticated server calls are intentionally skipped on logout (the
 * session is already gone).
 *
 * The monotonic logout epoch lets a continuation that was scheduled while
 * authenticated (an in-flight upload, a retrying delete) detect that logout has
 * since happened and refuse its post-logout server call: it snapshots
 * `getLogoutEpoch()` when scheduled and compares before acting.
 */
type LogoutTeardownListener = () => void;

const listeners = new Set<LogoutTeardownListener>();
let logoutEpoch = 0;

export function getLogoutEpoch(): number {
  return logoutEpoch;
}

export function onLogoutTeardown(listener: LogoutTeardownListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * May fire MORE THAN ONCE per sign-out by design: the explicit signOut() path
 * emits eagerly (supabase emits no SIGNED_OUT on sign-out errors) and the auth
 * event emits again on success. Listeners must be idempotent per sign-out;
 * the epoch is monotonic, so a double bump only tightens continuation guards.
 */
export function emitLogoutTeardown(): void {
  logoutEpoch += 1;
  // Snapshot before iterating: a listener that unsubscribes (itself or a
  // sibling) mid-emit must not perturb this emit's delivery set.
  const snapshot = [...listeners];
  for (const listener of snapshot) {
    // Best-effort delivery: this runs on the sign-out path, so one throwing
    // listener must neither starve the rest nor interrupt sign-out.
    try {
      listener();
    } catch (error) {
      // Listener errors originate in arbitrary consumer code and may carry
      // PII in their message — log the error name only (Sentry captures this).
      console.error(
        'logout teardown listener failed',
        error instanceof Error ? error.name : 'unknown',
      );
    }
  }
}
